import React, { useState } from "react";
import { Breadcrumbs, Pagination } from "@mantine/core";
import dayjs from "dayjs";
import { RRule } from "rrule";
import useGetBlockedDatesChangeLogApiService from "../../services/api/GetBlockedDatesChangeLogApiService";
import ProgressIndicator from "../../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../../common/pages/ErrorPage";
import DirectoryTable from "../../../../themes/components/DirectoryTable";
import { IBlockedDateChangeLogEntry } from "../../@types/blockedDates";

interface IBlockedDatesHistoryLogProps {
  setActiveBlockDateTab: (
    tab: "overview" | "calendar" | "edit" | "history"
  ) => void;
  blockTitle?: string;
  employeeId?: number;
  employeeName?: string;
}

interface IHistoryLogRow {
  id: number;
  entryId: string;
  timestamp: string;
  user: string;
  action: string;
  blockTitle: string;
  blockReason: string;
  startDate: string;
  endDate: string;
}

// Safety limit to prevent OOM on unbounded RRules
const RRULE_SAFETY_LIMIT = 1000;

const parseDatesFromRRule = (
  rruleString: string
): { startDate: string; endDate: string } => {
  try {
    const rule = RRule.fromString(rruleString);

    // Use a callback to limit the number of occurrences generated
    const dates = rule.all((date, i) => i < RRULE_SAFETY_LIMIT);

    if (dates.length === 0) return { startDate: "N/A", endDate: "N/A" };

    const startDate = dates[0];
    const endDate = dates[dates.length - 1];

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  } catch (error) {
    return { startDate: "N/A", endDate: "N/A" };
  }
};

interface BlockedDateChange {
  title: string;
  blockReason: string;
  startDate: string;
  endDate: string;
  changeType: string;
}

const extractBlockedDateChanges = (
  entry: IBlockedDateChangeLogEntry
): BlockedDateChange[] => {
  try {
    const changes = entry.changes?.config;
    if (!changes || changes.length < 2) return [];

    const before = changes[0];
    const after = changes[1];

    const beforeBlocks =
      before?.instant?.blocked_dates?.value ||
      before?.standard?.blocked_dates?.value ||
      [];
    const afterBlocks =
      after?.instant?.blocked_dates?.value ||
      after?.standard?.blocked_dates?.value ||
      [];

    const beforeIds = new Set(beforeBlocks.map((b: any) => b.id));
    const afterIds = new Set(afterBlocks.map((a: any) => a.id));

    const results: BlockedDateChange[] = [];

    // Find added blocks
    afterBlocks.forEach((block: any) => {
      if (!beforeIds.has(block.id)) {
        const { startDate, endDate } = parseDatesFromRRule(block.rrule);
        results.push({
          title: block.title || "N/A",
          blockReason: block.block_reason || "N/A",
          startDate,
          endDate,
          changeType: "added",
        });
      }
    });

    // Find removed blocks
    beforeBlocks.forEach((block: any) => {
      if (!afterIds.has(block.id)) {
        const { startDate, endDate } = parseDatesFromRRule(block.rrule);
        results.push({
          title: block.title || "N/A",
          blockReason: block.block_reason || "N/A",
          startDate,
          endDate,
          changeType: "removed",
        });
      }
    });

    // Find modified blocks
    afterBlocks.forEach((afterBlock: any) => {
      const beforeBlock = beforeBlocks.find((b: any) => b.id === afterBlock.id);
      if (beforeBlock) {
        const changedFields = [];
        if (afterBlock.title !== beforeBlock.title) changedFields.push("title");
        if (afterBlock.block_reason !== beforeBlock.block_reason)
          changedFields.push("block_reason");
        if (afterBlock.rrule !== beforeBlock.rrule) changedFields.push("dates");

        if (changedFields.length > 0) {
          const { startDate, endDate } = parseDatesFromRRule(afterBlock.rrule);
          results.push({
            title: afterBlock.title || "N/A",
            blockReason: afterBlock.block_reason || "N/A",
            startDate,
            endDate,
            changeType: `modified (${changedFields.join(", ")})`,
          });
        }
      }
    });

    return results;
  } catch (error) {
    return [];
  }
};

const BlockedDatesHistoryLog: React.FC<IBlockedDatesHistoryLogProps> = ({
  setActiveBlockDateTab,
  blockTitle,
  employeeId,
  employeeName,
}) => {
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<IHistoryLogRow[]>([]);

  const { data, isLoading, isError, error } =
    useGetBlockedDatesChangeLogApiService({
      page,
      employee_id: employeeId,
    });

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  const rows: IHistoryLogRow[] =
    data?.results.flatMap((entry) => {
      const changedBlocks = extractBlockedDateChanges(entry);
      if (changedBlocks.length === 0) return [];

      return changedBlocks.map((block, idx) => ({
        id: entry.id + idx,
        entryId: `${entry.id}-${idx}`,
        timestamp: dayjs(entry.timestamp).format("YYYY-MM-DD\nHH:mm:ss"),
        user: entry.actor_email || "Unknown",
        action: block.changeType,
        blockTitle: block.title,
        blockReason: block.blockReason,
        startDate: block.startDate,
        endDate: block.endDate,
      }));
    }) || [];

  return (
    <div>
      <Breadcrumbs
        separator=">"
        styles={{
          root: {
            margin: "20px 0",
          },
          breadcrumb: {
            cursor: "pointer",
            "&:not(:last-of-type)": {
              opacity: 0.5,
            },
          },
          separator: {
            opacity: 0.5,
          },
        }}
      >
        {[
          {
            title: "Overview",
            onClick: () => setActiveBlockDateTab("overview"),
          },
          ...(employeeId && blockTitle
            ? [
                {
                  title: blockTitle,
                  onClick: () => setActiveBlockDateTab("edit"),
                },
              ]
            : []),
          {
            title: (() => {
              if (employeeId && employeeName) {
                return `${employeeName} - History Update Log`;
              }
              if (blockTitle) {
                return `${blockTitle} - History Update Log`;
              }
              return "History Update Log";
            })(),
          },
        ].map((item) => (
          <button
            key={item.title}
            onClick={(event) => {
              event.preventDefault();
              if (item.onClick) {
                item.onClick();
              }
            }}
            className="p-0 m-0 bg-transparent border-0 appearance-none cursor-pointer text-inherit font-inherit"
            type="button"
          >
            {item.title}
          </button>
        ))}
      </Breadcrumbs>

      <DirectoryTable<IHistoryLogRow>
        headers={[
          "Timestamp",
          "User",
          "Action",
          "Block Title",
          "Block reason",
          "Start Date",
          "End Date",
        ]}
        data={rows}
        rows={rows.map((row) => ({
          timestamp: row.timestamp,
          user: row.user,
          action: row.action,
          blockTitle: row.blockTitle,
          blockReason: row.blockReason,
          startDate: row.startDate,
          endDate: row.endDate,
        }))}
        selection={selection}
        onSelect={setSelection}
        hideSelectControls
      />

      {data && data.pages > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination
            page={page}
            onChange={setPage}
            total={data.pages}
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

export default BlockedDatesHistoryLog;
